import type {
  ArticleActivityPeriod,
  ArticleMatchKind,
  ArticleQtyYtdStatus,
} from "../api/articles";
import { formatDateShort, formatQty } from "./format";

export type ArticleActivitySummary = {
  invoice_document_count_12m: number;
  qty_ytd: string | number | null;
  qty_ytd_uom: string | null;
  qty_ytd_status: ArticleQtyYtdStatus;
};

const MATCH_KIND_LABELS: Record<ArticleMatchKind, string> = {
  exact_code: "Exakte Artikelnummer",
  exact_alias: "Exakte alternative Nummer",
  code_prefix: "Beginn der Artikelnummer",
  alias_prefix: "Beginn der alternativen Nummer",
  code_contains: "Teil der Artikelnummer",
  alias_contains: "Teil der alternativen Nummer",
  product_family: "Produktfamilie",
  description: "Beschreibung",
};

export function articleMatchKindLabel(
  matchKind: ArticleMatchKind | null,
): string | null {
  return matchKind ? MATCH_KIND_LABELS[matchKind] : null;
}

export function formatInvoiceDocumentCount12m(
  count: number | null | undefined,
): string {
  if (!Number.isFinite(count)) return "–";
  return Math.max(0, Math.trunc(count ?? 0)).toLocaleString("de-AT");
}

export function formatInvoiceActivity12m(
  count: number | null | undefined,
): string {
  const countLabel = formatInvoiceDocumentCount12m(count);
  if (countLabel === "–") return "– Einkäufe · 12 M.";
  const safeCount = Math.max(0, Math.trunc(count ?? 0));
  return `${safeCount.toLocaleString("de-AT")} ${
    safeCount === 1 ? "Einkauf" : "Einkäufe"
  } · 12 M.`;
}

export function formatArticleQtyYtd(
  article: Pick<
    ArticleActivitySummary,
    "qty_ytd" | "qty_ytd_status" | "qty_ytd_uom"
  >,
): string {
  switch (article.qty_ytd_status) {
    case "available": {
      if (article.qty_ytd == null || !article.qty_ytd_uom) {
        return "Nicht verfügbar";
      }
      return `${formatQty(article.qty_ytd)} ${article.qty_ytd_uom}`;
    }
    case "mixed":
      return "Gemischte Einheiten";
    case "unit_missing":
      return "Einheit fehlt";
    case "unavailable":
      return "Nicht verfügbar";
    default:
      return "Nicht verfügbar";
  }
}

export function activityPeriodHint(
  period: ArticleActivityPeriod | null | undefined,
  metric: "rolling12m" | "ytd",
): string {
  if (!period) {
    return metric === "rolling12m"
      ? "Rollierende zwölf Monate; nur unterschiedliche Rechnungsbelege, keine Gutschriften."
      : "Kalenderjahr bis zum Datenstand; Mengen nur bei eindeutiger Einheit, keine Gutschriften.";
  }
  const from = metric === "rolling12m" ? period.rolling_12m_from : period.ytd_from;
  const range = `${formatDateShort(from)} bis ${formatDateShort(period.as_of_date)}`;
  return metric === "rolling12m"
    ? `${range}; nur unterschiedliche Rechnungsbelege, keine Gutschriften.`
    : `${range}; Mengen nur bei eindeutiger Einheit, keine Gutschriften.`;
}
